/**
 * Client-side twin of
 * `baserow/contrib/database/fields/social_handles.py`.
 *
 * The social handle field stores the canonical profile URL and derives the
 * `@handle` at render time. This module holds both halves, because the
 * normaliser has to run in the browser too: `prepareValueForPaste` serves both
 * grid bulk-paste and the spreadsheet importer, and today the equivalent
 * `URLFieldType.prepareValueForPaste` returns `''` for anything that is not
 * already a URL - so pasting `@nike`, `nike` and `instagram.com/nike` silently
 * blanks two of the three.
 *
 * Keep this in step with the Python module. The backend re-normalises every
 * write, so a drift here degrades to "the server fixed it up", never to a
 * wrong stored value.
 */

export const INSTAGRAM = 'instagram'
export const LINKEDIN = 'linkedin'
export const FACEBOOK = 'facebook'
export const X = 'x'
export const YOUTUBE = 'youtube'
export const TIKTOK = 'tiktok'
export const SNAPCHAT = 'snapchat'
export const THREADS = 'threads'
export const PINTEREST = 'pinterest'
export const TWITCH = 'twitch'
export const AUTO = 'auto'

export const DEFAULT_PLATFORM = INSTAGRAM

export const PLATFORMS = {
  [INSTAGRAM]: {
    type: INSTAGRAM,
    host: 'instagram.com',
    domains: ['instagram.com', 'instagr.am'],
    pathPrefix: '',
    caseFoldPath: true,
    iconClass: 'baserow-icon-instagram',
    reserved: [
      'explore',
      'reels',
      'reel',
      'stories',
      'direct',
      'accounts',
      'p',
      'tv',
    ],
  },
  [LINKEDIN]: {
    type: LINKEDIN,
    host: 'linkedin.com',
    domains: ['linkedin.com'],
    pathPrefix: 'in/',
    caseFoldPath: true,
    collapseToCanonicalHost: true,
    iconClass: 'baserow-icon-linkedin',
    reserved: [
      'feed',
      'jobs',
      'mynetwork',
      'messaging',
      'notifications',
      'learning',
    ],
  },
  [FACEBOOK]: {
    type: FACEBOOK,
    host: 'facebook.com',
    domains: ['facebook.com', 'fb.com', 'fb.me', 'fb.watch'],
    pathPrefix: '',
    // Facebook page vanity names are shown as written.
    caseFoldPath: false,
    iconClass: 'baserow-icon-facebook',
    reserved: [
      'home.php',
      'marketplace',
      'groups',
      'watch',
      'events',
      'gaming',
    ],
  },
  [X]: {
    type: X,
    host: 'x.com',
    // twitter.com resolves to X for detection, but the host is never rewritten.
    // `twitter.svg` in the icon set is already the X glyph, so it is reused.
    domains: ['x.com', 'twitter.com'],
    pathPrefix: '',
    caseFoldPath: true,
    iconClass: 'baserow-icon-twitter',
    reserved: [
      'home',
      'explore',
      'notifications',
      'messages',
      'i',
      'search',
      'settings',
    ],
  },
  [YOUTUBE]: {
    type: YOUTUBE,
    host: 'youtube.com',
    domains: ['youtube.com', 'youtu.be'],
    pathPrefix: '@',
    // `UC…` channel ids and the legacy /c/ and /user/ segments are
    // case-sensitive, so YouTube paths are never folded.
    caseFoldPath: false,
    iconClass: 'baserow-icon-youtube',
    reserved: ['feed', 'watch', 'results', 'playlist'],
  },
  [TIKTOK]: {
    type: TIKTOK,
    host: 'tiktok.com',
    domains: ['tiktok.com'],
    pathPrefix: '@',
    caseFoldPath: true,
    forceAtInPath: true,
    iconClass: 'baserow-icon-tiktok',
    reserved: ['foryou', 'following', 'live', 'explore', 'upload'],
  },
  [SNAPCHAT]: {
    type: SNAPCHAT,
    host: 'snapchat.com',
    domains: ['snapchat.com'],
    // Verified live: snapchat.com/add/<user> 308s to /@<user>, so /@<user> is
    // the canonical form today and /add/ is the legacy one, rewritten below.
    pathPrefix: '@',
    caseFoldPath: true,
    collapseToCanonicalHost: true,
    forceAtInPath: true,
    pathRewrites: [[/^\/add\//, '/@']],
    iconClass: 'baserow-icon-snapchat',
    // `/t/<code>` is a share link with an opaque code, so no handle is derived.
    reserved: ['add', 't', 'discover', 'spotlight', 'lens'],
  },
  [THREADS]: {
    type: THREADS,
    // threads.net 301s to threads.com, so .com is canonical today even though
    // .net was the original.
    host: 'threads.com',
    domains: ['threads.com', 'threads.net'],
    pathPrefix: '@',
    caseFoldPath: true,
    collapseToCanonicalHost: true,
    forceAtInPath: true,
    iconClass: 'baserow-icon-threads',
    reserved: ['search', 'activity', 'login'],
  },
  [PINTEREST]: {
    type: PINTEREST,
    host: 'pinterest.com',
    domains: ['pinterest.com', 'pin.it'],
    // Country mirrors are separate TLDs (pinterest.co.uk) as well as
    // subdomains (in.pinterest.com), so a suffix match cannot find them all.
    hostRegex: /^([a-z0-9-]+\.)*pinterest\.[a-z.]+$/,
    pathPrefix: '',
    caseFoldPath: true,
    collapseToCanonicalHost: true,
    iconClass: 'baserow-icon-pinterest',
    reserved: ['pin', 'search', 'ideas', 'today', 'settings', 'news'],
  },
  [TWITCH]: {
    type: TWITCH,
    host: 'twitch.tv',
    domains: ['twitch.tv'],
    pathPrefix: '',
    caseFoldPath: true,
    // m.twitch.tv is the same channel as twitch.tv.
    collapseToCanonicalHost: true,
    iconClass: 'baserow-icon-twitch',
    reserved: [
      'directory',
      'videos',
      'settings',
      'following',
      'p',
      'downloads',
    ],
  },
}

export const PLATFORM_CHOICES = [
  { value: INSTAGRAM, name: 'Instagram' },
  { value: LINKEDIN, name: 'LinkedIn' },
  { value: FACEBOOK, name: 'Facebook' },
  { value: X, name: 'X' },
  { value: YOUTUBE, name: 'YouTube' },
  { value: TIKTOK, name: 'TikTok' },
  { value: SNAPCHAT, name: 'Snapchat' },
  { value: THREADS, name: 'Threads' },
  { value: PINTEREST, name: 'Pinterest' },
  { value: TWITCH, name: 'Twitch' },
  { value: AUTO, name: 'Auto-detect' },
]

/**
 * Hosts whose path is an opaque short code rather than a profile path. They are
 * never case-folded, never get TikTok's mandatory `@`, and yield no handle.
 * They are also never expanded - that would be a network call inside a cell
 * write.
 */
export const SHORT_LINK_HOSTS = [
  'vm.tiktok.com',
  'vt.tiktok.com',
  'youtu.be',
  'fb.me',
  'fb.watch',
  'pin.it',
]

const BLOCKED_SCHEMES = ['javascript', 'data', 'vbscript']

/**
 * The only positional restriction that is real is on **dots**: no leading dot,
 * no trailing dot, and no consecutive dots. Underscores and hyphens are
 * unrestricted and may start or end a handle - `_gypsycouple`,
 * `thepotraitvibe_` and `_candyfloss_____` are all live Instagram accounts.
 *
 * An earlier version required a trailing alphanumeric, which applied the dot
 * rule to `_` and `-` too and rejected 6.3% of the production creator list.
 * Dots are never stripped either: `shivam.gupta` and `shivamgupta` are
 * different Instagram accounts.
 *
 * Must stay character-for-character in step with `HANDLE_RE` and
 * `MAX_HANDLE_LENGTH` in
 * `baserow/contrib/database/fields/social_handles.py`, and with the bare-handle
 * test in the PL/pgSQL that module generates. If this accepted a handle the
 * backend rejected, the UI would tell the user a value was fine and the column
 * conversion would then blank it.
 */
const HANDLE_RE = /^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*$/

/** Generous upper bound; the longest real limit is YouTube's 30. */
const MAX_HANDLE_LENGTH = 64
const SCHEME_RE = /^([A-Za-z][A-Za-z0-9+.-]*):/
const FULL_SCHEME_RE = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//
// Alternation rather than a character class, which is what this was. The class
// behaved correctly -- six independent code points, any one of which matches --
// but `\u200D` is ZERO WIDTH JOINER, and sitting between two other escapes it
// reads to `no-misleading-character-class` as the start of a joined sequence
// (the rule cannot tell a class that happens to contain ZWJ from one meaning to
// match an emoji built with it). Alternation states the same six one-character
// matches with no sequence to misread.
const INVISIBLE_RE = /\u200B|\u200C|\u200D|\u2060|\uFEFF|\u00AD/g

export const HANDLE_KIND_HANDLE = 'handle'
export const HANDLE_KIND_COMPANY = 'company'
export const HANDLE_KIND_CHANNEL = 'channel'
export const HANDLE_KIND_PROFILE_ID = 'profile_id'
export const HANDLE_KIND_UNKNOWN = 'unknown'

export function getPlatformConfig(platform) {
  return PLATFORMS[platform] || null
}

function hostMatches(host, domain) {
  return host === domain || host.endsWith('.' + domain)
}

export function detectPlatformFromHost(host) {
  const lowered = (host || '').toLowerCase()
  for (const platform of Object.values(PLATFORMS)) {
    if (platform.domains.some((domain) => hostMatches(lowered, domain))) {
      return platform.type
    }
  }
  // Only consulted after the cheap exact/suffix pass, so it costs nothing for
  // the platforms that do not need it.
  for (const platform of Object.values(PLATFORMS)) {
    if (platform.hostRegex && platform.hostRegex.test(lowered)) {
      return platform.type
    }
  }
  return null
}

export function cleanRawValue(value) {
  let text = String(value)
    .replace(INVISIBLE_RE, '')
    .replace(/[\n\r\t]+/g, ' ')
    .trim()
  // Mail clients and chat apps wrap links in brackets, and a handle copied out
  // of a sentence keeps the punctuation glued to it.
  text = text.replace(/^[(<[]+/, '').replace(/[.,)>\]]+$/, '')
  return text.trim()
}

/**
 * A dot alone does not make something a URL - `shivam.gupta` is an ordinary
 * Instagram handle - so a dotted string only counts when its host is one we
 * actually recognise.
 */
export function looksLikeUrl(text) {
  if (text.includes('://') || text.startsWith('//') || text.includes('/')) {
    return true
  }
  return detectPlatformFromHost(text.toLowerCase()) !== null
}

function splitUrl(raw) {
  const withScheme = FULL_SCHEME_RE.test(raw)
    ? raw
    : 'https://' + raw.replace(/^\/+/, '')
  // `URL` is not used: it percent-encodes and reorders in ways that would
  // change values we mean to leave alone, and it throws on inputs we want to
  // preserve rather than reject.
  const withoutFragment = withScheme.split('#')[0]
  const questionMark = withoutFragment.indexOf('?')
  const query =
    questionMark === -1 ? '' : withoutFragment.slice(questionMark + 1)
  const beforeQuery =
    questionMark === -1
      ? withoutFragment
      : withoutFragment.slice(0, questionMark)

  const rest = beforeQuery.replace(FULL_SCHEME_RE, '').replace(/^\/+/, '')
  const slash = rest.indexOf('/')
  let host = (slash === -1 ? rest : rest.slice(0, slash)).toLowerCase()
  // Drop any `user:pass@` prefix and port; neither belongs in a profile URL and
  // the first is credential-leak shaped.
  host = host.replace(/^[^@]*@/, '').split(':')[0]
  const path = slash === -1 ? '' : rest.slice(slash)
  return { host, path, query }
}

function getQueryParam(query, name) {
  for (const pair of query.split('&')) {
    const [key, value] = pair.split('=')
    if (decodeURIComponent(key || '') === name) {
      return decodeURIComponent(value || '')
    }
  }
  return null
}

function normalizeHandle(text, platform) {
  const handle = text.replace(/^@+/, '')
  if (handle.length > MAX_HANDLE_LENGTH || !HANDLE_RE.test(handle)) {
    // Never blanked: the raw string is handed back so the cell keeps what the
    // user typed and the client can flag it.
    return { value: text, valid: false }
  }

  const config = getPlatformConfig(platform)
  if (config === null) {
    // `auto` cannot know which network a bare handle belongs to, so there is no
    // URL to build. Kept verbatim and rendered as un-linked text.
    return { value: '@' + handle, valid: true }
  }

  const cased = config.caseFoldPath ? handle.toLowerCase() : handle
  return {
    value: `https://${config.host}/${config.pathPrefix}${cased}`,
    valid: true,
  }
}

function normalizeUrl(text, platform) {
  const { host: rawHost, path: rawPath, query } = splitUrl(text)
  if (rawHost === '') {
    return { value: text, valid: false }
  }

  let host = rawHost.replace(/^www\./, '')
  const detected = detectPlatformFromHost(host)
  const detectedConfig = getPlatformConfig(detected)
  const isShortLink = SHORT_LINK_HOSTS.includes(host)
  if (
    detectedConfig !== null &&
    detectedConfig.collapseToCanonicalHost &&
    !isShortLink
  ) {
    // Every mirror of the same profile becomes one value: LinkedIn country
    // subdomains, Pinterest country TLDs, m.twitch.tv, threads.net.
    host = detectedConfig.host
  }

  let path = rawPath.replace(/\/{2,}/g, '/').replace(/\/+$/, '')

  // Case folding is decided by the host when we recognise it, so a YouTube link
  // pasted into an Instagram column keeps its case-sensitive channel id.
  const config = detectedConfig || getPlatformConfig(platform)
  if (config !== null && config.caseFoldPath && !isShortLink) {
    path = path.toLowerCase()
  }

  // Path forms the platform itself redirects, e.g. Snapchat's legacy
  // /add/<user>, which returns a 308 to /@<user>.
  if (config !== null && !isShortLink && config.pathRewrites) {
    for (const [pattern, replacement] of config.pathRewrites) {
      path = path.replace(pattern, replacement)
    }
  }

  // The whole query string goes, which kills igsh, mibextid, si, _t/_r, trk and
  // utm_* in one rule - a privacy fix as much as a normalisation, since those
  // are share ids tying the link to whoever copied it.
  let suffix = ''

  // The one exception: for Facebook's profile.php the query string IS the
  // identity. Miss it and every numeric profile becomes a bare profile.php.
  if (detected === FACEBOOK && path.toLowerCase().endsWith('/profile.php')) {
    path = path.slice(0, path.length - 'profile.php'.length) + 'profile.php'
    const id = getQueryParam(query, 'id')
    if (id !== null && /^[0-9]+$/.test(id)) {
      suffix = '?id=' + id
    }
  }

  // TikTok, Threads and Snapchat profile paths always carry the @;
  // tiktok.com/nike is not a reachable profile while tiktok.com/@nike is.
  const resolvedConfig = detectedConfig || getPlatformConfig(platform)
  if (resolvedConfig !== null && resolvedConfig.forceAtInPath && !isShortLink) {
    const segments = path.split('/').filter((s) => s !== '')
    if (
      segments.length === 1 &&
      !segments[0].startsWith('@') &&
      !resolvedConfig.reserved.includes(segments[0])
    ) {
      path = '/@' + segments[0]
    }
  }

  return { value: `https://${host}${path}${suffix}`, valid: true }
}

/**
 * Turns anything a user can plausibly supply into the canonical profile URL.
 *
 * Returns `{ value, valid }`. `valid` is false when the input could not be
 * parsed - `value` then still carries the cleaned raw string, because losing
 * what the user typed is never the right answer.
 */
export function normalizeSocialHandle(value, platform = AUTO) {
  if (value === null || value === undefined) {
    return { value: '', valid: true }
  }

  const text = cleanRawValue(value)
  if (text === '') {
    return { value: '', valid: true }
  }

  const schemeMatch = SCHEME_RE.exec(text)
  if (schemeMatch && BLOCKED_SCHEMES.includes(schemeMatch[1].toLowerCase())) {
    // These would become a live href in the grid, so they are the one case that
    // is emptied rather than preserved.
    return { value: '', valid: false }
  }

  return looksLikeUrl(text)
    ? normalizeUrl(text, platform)
    : normalizeHandle(text, platform)
}

/**
 * Derives the display form from a stored canonical URL. Never persisted - this
 * runs on every render, exactly as `formatDuration` does.
 */
export function deriveHandle(value, platform = AUTO) {
  const empty = { handle: null, kind: HANDLE_KIND_UNKNOWN, platform: null }
  if (!value) {
    return empty
  }

  const text = String(value).trim()
  if (text === '') {
    return empty
  }

  if (!FULL_SCHEME_RE.test(text)) {
    // An `auto` bare handle is stored as `@handle` with no URL around it.
    const handle = text.replace(/^@+/, '')
    if (handle.length <= MAX_HANDLE_LENGTH && HANDLE_RE.test(handle)) {
      const config = getPlatformConfig(platform)
      return {
        handle,
        kind: HANDLE_KIND_HANDLE,
        platform: config ? config.type : null,
      }
    }
    return empty
  }

  const { host: rawHost, path, query } = splitUrl(text)
  const host = rawHost.replace(/^www\./, '')
  const detected = detectPlatformFromHost(host)
  const resolved = detected || (PLATFORMS[platform] ? platform : null)
  const segments = path.split('/').filter((s) => s !== '')

  const result = { handle: null, kind: HANDLE_KIND_UNKNOWN, platform: resolved }

  if (segments.length === 0 || SHORT_LINK_HOSTS.includes(host)) {
    // A short link's path is an opaque code, so there is no handle to show.
    return result
  }

  const first = segments[0]

  if (resolved === FACEBOOK && first.toLowerCase() === 'profile.php') {
    // A numeric Facebook profile has no handle at all.
    return {
      handle: getQueryParam(query, 'id'),
      kind: HANDLE_KIND_PROFILE_ID,
      platform: resolved,
    }
  }

  if (resolved === LINKEDIN && segments.length > 1) {
    if (first === 'company') {
      return {
        handle: segments[1],
        kind: HANDLE_KIND_COMPANY,
        platform: resolved,
      }
    }
    if (first === 'in') {
      return {
        handle: segments[1],
        kind: HANDLE_KIND_HANDLE,
        platform: resolved,
      }
    }
  }

  if (resolved === YOUTUBE && segments.length > 1) {
    if (first === 'channel') {
      return {
        handle: segments[1],
        kind: HANDLE_KIND_CHANNEL,
        platform: resolved,
      }
    }
    if (first === 'c' || first === 'user') {
      return {
        handle: segments[1],
        kind: HANDLE_KIND_HANDLE,
        platform: resolved,
      }
    }
  }

  const config = getPlatformConfig(resolved)
  if (
    config !== null &&
    config.reserved.includes(first.replace(/^@+/, '').toLowerCase())
  ) {
    // Site chrome, not a profile.
    return result
  }

  return {
    handle: first.replace(/^@+/, ''),
    kind: HANDLE_KIND_HANDLE,
    platform: resolved,
  }
}

/**
 * Everything a cell needs to render, in one call, so all four components stay
 * consistent.
 */
/**
 * The platform a value's host clearly belongs to, when that is not the platform
 * the field is set to. Returns null when they agree, when the field is on
 * `auto` (which resolves per cell by design), or when the host is not one we
 * recognise.
 *
 * The value is never rewritten to the field's platform - that would invent a
 * profile URL that does not exist. It is kept exactly as normalised for the
 * network it actually belongs to, and flagged.
 */
export function getPlatformMismatch(value, platform = AUTO) {
  if (!value || platform === AUTO || !PLATFORMS[platform]) {
    return null
  }
  const text = String(value)
  if (!FULL_SCHEME_RE.test(text)) {
    return null
  }
  const { host } = splitUrl(text)
  const detected = detectPlatformFromHost(host.replace(/^www\./, ''))
  return detected !== null && detected !== platform ? detected : null
}

export function getSocialHandleDisplay(value, platform = AUTO) {
  if (value === null || value === undefined || value === '') {
    return {
      text: '',
      href: null,
      iconClass: null,
      kind: null,
      unparsed: false,
      mismatch: null,
      platform: null,
    }
  }

  const raw = String(value)
  const derived = deriveHandle(raw, platform)
  const config =
    getPlatformConfig(derived.platform) || getPlatformConfig(platform)
  // A static, locally bundled icon driven by the resolved platform. Favicons
  // are deliberately never fetched: that would hand the user's data to six
  // different platforms on every render of every grid row.
  const iconClass = config ? config.iconClass : 'iconoir-at-sign'
  const href = FULL_SCHEME_RE.test(raw) ? raw : null
  const mismatch = getPlatformMismatch(raw, platform)
  const common = {
    href,
    iconClass,
    kind: derived.kind,
    mismatch,
    platform: derived.platform,
  }

  if (derived.kind === HANDLE_KIND_PROFILE_ID) {
    // `profile.php?id=…` carries no handle - the id is shown, not a fake `@`.
    return { ...common, text: derived.handle || '', unparsed: false }
  }

  if (derived.handle === null) {
    // Did not parse into anything we can name. The raw string is shown as-is:
    // never hidden, never blanked.
    return { ...common, text: raw, unparsed: true }
  }

  // A LinkedIn company reads as `foo` with a company glyph, not `@foo`, and a
  // YouTube channel id is not a handle either.
  const text =
    derived.kind === HANDLE_KIND_COMPANY || derived.kind === HANDLE_KIND_CHANNEL
      ? derived.handle
      : '@' + derived.handle

  return { ...common, text, unparsed: false }
}

/**
 * Human-readable platform names, for the mismatch tooltip.
 */
export function getPlatformName(platform) {
  const choice = PLATFORM_CHOICES.find((c) => c.value === platform)
  return choice ? choice.name : platform
}
